<template>
  <div class="blogpost-bg">
    <template v-if="blogStore.currentBlog">
      <!-- Hero Section -->
      <div class="blogpost-hero">
        <img
          :src="blogStore.currentBlog?.image || '/placeholder.jpg'"
          :alt="blogStore.currentBlog?.title"
          @error="e => { if (!e.target.src.endsWith('/placeholder.jpg')) e.target.src = '/placeholder.jpg'; }"
        />
        <div class="blogpost-hero-overlay"></div>
        <div v-if="blogStore.currentBlog?.category" class="blogpost-category-badge">
          {{ blogStore.currentBlog.category }}
        </div>
        <div class="blogpost-hero-title">
          <h1>{{ blogStore.currentBlog?.title }}</h1>
        </div>
      </div>

      <!-- Main Content -->
      <div class="blogpost-container">
        <!-- Meta Row -->
        <div class="blogpost-meta">
          <div class="meta-item">
            <i class="fa fa-calendar"></i>
            <span>{{ formatDate(blogStore.currentBlog?.created_at) }}</span>
          </div>
          <div v-if="blogStore.currentBlog?.author" class="meta-item">
            <i class="fa fa-user"></i>
            <span>{{ blogStore.currentBlog.author }}</span>
          </div>
          <div v-if="Array.isArray(blogStore.currentBlog?.tags) && blogStore.currentBlog.tags.length" class="meta-item">
            <i class="fa fa-tag"></i>
            <span>{{ blogStore.currentBlog.tags.join(', ') }}</span>
          </div>
        </div>

        <!-- After the meta row, add: -->
        <div v-if="Array.isArray(blogStore.currentBlog?.tags) && blogStore.currentBlog.tags.length" class="blogpost-tags-row">
          <span
            v-for="tag in blogStore.currentBlog.tags"
            :key="tag"
            class="blogpost-tag-badge"
            @click="handleTagClick(tag)"
            style="cursor:pointer"
          >
            #{{ tag }}
          </span>
        </div>

        <!-- Blog Content -->
        <article class="blogpost-article">
          <div v-html="formattedContent"></div>
        </article>

        <!-- Share Row -->
        <div class="blogpost-share">
          <span class="blogpost-share-label">Share this post:</span>
          <div class="blogpost-share-icons">
            <a :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-facebook-f"></i>
            </a>
            <a :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(blogStore.currentBlog?.title)}`" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-twitter"></i>
            </a>
            <a :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-linkedin-in"></i>
            </a>
            <a :href="`https://wa.me/?text=${encodeURIComponent(blogStore.currentBlog?.title + ' ' + currentUrl)}`" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-whatsapp"></i>
            </a>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="blogpost-container" style="text-align:center; padding: 60px 0;">
        <h2>Blog not found</h2>
        <p>The blog you are looking for does not exist or could not be loaded.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBlogStore } from '../stores/blogStore'
import BlogCard from './BlogCard.vue'

const route = useRoute()
const router = useRouter()
const blogStore = useBlogStore()
const currentUrl = window.location.href;

const handleTagClick = (tag) => {
  router.push({ path: '/blogs', query: { tag } })
}

const formattedContent = computed(() => {
  if (!blogStore.currentBlog?.content) return ''
  let content = blogStore.currentBlog.content
  content = content.replace(/\r\n/g, '\n')
  const paragraphs = content.split('\n\n')
  return paragraphs
    .map(paragraph => {
      paragraph = paragraph.trim()
      if (!paragraph) return ''
      if (paragraph.match(/^[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}].*$/u)) {
        return `<h3 class="content-heading">${paragraph}</h3>`
      }
      return `<p>${paragraph}</p>`
    })
    .filter(p => p)
    .join('')
})

const relatedBlogs = computed(() => {
  if (!blogStore.currentBlog) return []
  return blogStore.blogs
    .filter(blog => 
      blog.id !== blogStore.currentBlog.id && 
      blog.category === blogStore.currentBlog.category
    )
    .slice(0, 2)
})

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const setCurrentBlogBySlug = (slug) => {
  const found = blogStore.blogs.find(b => b.slug === slug)
  if (found) blogStore.setCurrentBlog(found)
  else blogStore.setCurrentBlog(null)
}

const loadBlog = async () => {
  const slug = route.params.slug
  if (!blogStore.blogs.length) {
    await blogStore.fetchBlogs()
  }
  setCurrentBlogBySlug(slug)
}

onMounted(() => {
  loadBlog()
})

watch(() => route.params.slug, (newSlug) => {
  setCurrentBlogBySlug(newSlug)
})
</script>

<style scoped>
.blogpost-bg {
  min-height: 100vh;
  background: #f3f4f6;
  padding-bottom: 40px;
}

.blogpost-hero {
  position: relative;
  height: 340px;
  width: 100%;
  overflow: hidden;
}

.blogpost-hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: brightness(0.7);
}

.blogpost-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.15) 100%);
  pointer-events: none;
}

.blogpost-category-badge {
  position: absolute;
  top: 2rem;
  left: 2rem;
  background: linear-gradient(90deg, #2563eb 60%, #3b82f6 100%);
  color: #fff;
  font-size: 1.15rem;
  font-weight: 800;
  padding: 0.55em 1.5em;
  border-radius: 9999px;
  letter-spacing: 0.08em;
  box-shadow: 0 4px 16px rgba(44,62,80,0.18), 0 0 0 3px #fff;
  text-transform: uppercase;
  border: 2px solid #fff;
  z-index: 3;
  transition: transform 0.15s, box-shadow 0.15s;
}

.blogpost-category-badge:hover {
  transform: scale(1.07) translateY(-2px);
  box-shadow: 0 8px 32px rgba(44,62,80,0.22), 0 0 0 3px #2563eb;
  cursor: pointer;
}

.blogpost-hero-title {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blogpost-hero-title h1 {
  color: #fff;
  font-size: 2.7rem;
  font-weight: bold;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.2;
  text-shadow: 0 4px 24px rgba(0,0,0,0.35), 0 1.5px 0 #000;
  letter-spacing: -1px;
}

@media (min-width: 768px) {
  .blogpost-hero {
    height: 400px;
  }
  .blogpost-hero-title h1 {
    font-size: 3.5rem;
  }
}

.blogpost-container {
  max-width: 1100px;
  margin: 80px auto 80px auto;
  padding: 3.5rem 2.5rem 2.5rem 2.5rem;
  background: #fff;
  border-radius: 28px;
  box-shadow: 0 12px 48px rgba(44, 62, 80, 0.13);
  position: relative;
  z-index: 2;
  animation: fadeIn 0.8s cubic-bezier(.4,0,.2,1);
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px);}
  to { opacity: 1; transform: none;}
}

.blogpost-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  color: #4b5563;
  font-size: 1.13rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.blogpost-meta .meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.blogpost-article {
  font-size: 1.28rem;
  line-height: 2.1;
  color: #222;
  margin-bottom: 2.5rem;
  text-align: left;
  padding: 0.5rem 0;
}

.blogpost-article p {
  margin-bottom: 2.2em;
  color: #2d3748;
  font-size: 1.18rem;
  letter-spacing: 0.01em;
}

.blogpost-article h2, .blogpost-article h3 {
  color: #1e293b;
  font-weight: 700;
  margin-top: 2.5em;
  margin-bottom: 1em;
  border-left: 4px solid #2563eb;
  padding-left: 0.7em;
  background: #f1f5f9;
  border-radius: 0 8px 8px 0;
}

.blogpost-article h1,
.blogpost-article h4,
.blogpost-article h5,
.blogpost-article h6 {
  margin-top: 2em;
  margin-bottom: 1em;
  line-height: 1.3;
}

.blogpost-article ul,
.blogpost-article ol {
  margin-bottom: 1.7em;
  padding-left: 2em;
}

.blogpost-article blockquote {
  margin: 1.7em 0;
  padding: 1em 1.5em;
  background: #f3f4f6;
  border-left: 4px solid #2563eb;
  color: #374151;
}

.blogpost-share {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.blogpost-share-label {
  color: #4b5563;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.blogpost-share-icons {
  display: flex;
  gap: 1.2rem;
}

.blogpost-share-icons a {
  padding: 0.7rem;
  border-radius: 9999px;
  transition: background 0.2s, transform 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.blogpost-share-icons a:hover {
  background: #e0e7ff;
  transform: translateY(-2px) scale(1.15);
}

.blogpost-tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7em;
  margin-bottom: 1.5em;
}

.blogpost-tag-badge {
  background: #e0e7ff;
  color: #2563eb;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.35em 1em;
  border-radius: 9999px;
  letter-spacing: 0.01em;
  transition: background 0.2s, color 0.2s;
}

.blogpost-tag-badge:hover {
  background: #2563eb;
  color: #fff;
}
</style> 